import { useState } from 'react';
import { useInventory } from '@/api/queries';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { cn } from '@/lib/cn';
import { hapticNotify } from '@/lib/telegram';
import type { InventoryItem, UsableItemId } from '@/types';

const DIRECT_USE: UsableItemId[] = ['energy_vial', 'tent'];
const WAIFU_TARGETED: UsableItemId[] = ['health_potion', 'coffee', 'chocolate', 'book'];

interface ItemTooltipState {
  item: InventoryItem;
  rect: DOMRect;
}

export function InventoryGrid() {
  const userId = useAuthStore((s) => s.userId);
  const openModal = useUIStore((s) => s.openModal);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const { data, isLoading } = useInventory(userId);
  const inventory = data?.inventory ?? [];
  const [tooltip, setTooltip] = useState<ItemTooltipState | null>(null);

  const handleClick = (item: InventoryItem) => {
    if (!item.usable) {
      pushNotification({ type: 'info', message: `${item.name} нельзя использовать вручную` });
      return;
    }
    const id = item.item_id as UsableItemId;
    if (DIRECT_USE.includes(id)) {
      openModal('item-confirm', { itemId: id, item });
    } else if (WAIFU_TARGETED.includes(id)) {
      openModal('item-target-waifu', { itemId: id, item });
    } else if (id === 'limit_breaker') {
      pushNotification({
        type: 'info',
        message: 'Используй Limit Breaker из экрана waifu',
      });
    } else {
      hapticNotify('warning');
      pushNotification({ type: 'warning', message: `Неизвестный предмет: ${item.name}` });
    }
  };

  if (isLoading) {
    return <div className="text-white/40 text-sm">Загружаем инвентарь…</div>;
  }
  if (inventory.length === 0) {
    return <div className="text-white/40 text-sm">Инвентарь пуст.</div>;
  }

  return (
    <>
      <div className="grid grid-cols-4 gap-2">
        {inventory.map((item) => (
          <button
            key={item.item_id}
            className={cn(
              'aspect-square rounded-xl bg-bg-elevated border border-white/5',
              'flex items-center justify-center relative active:scale-95 transition',
              !item.usable && 'opacity-60',
            )}
            onClick={() => handleClick(item)}
            onTouchStart={(e) =>
              setTooltip({ item, rect: e.currentTarget.getBoundingClientRect() })
            }
            onTouchEnd={() => setTimeout(() => setTooltip(null), 1500)}
            onMouseEnter={(e) =>
              setTooltip({ item, rect: e.currentTarget.getBoundingClientRect() })
            }
            onMouseLeave={() => setTooltip(null)}
          >
            <img
              src={`/data/items/${item.icon}`}
              alt={item.name}
              className="w-16 h-16 object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            {item.stackable && item.quantity > 1 && (
              <span className="absolute bottom-1 right-1 text-[10px] font-bold text-white bg-bg/80 rounded px-1">
                {item.quantity}
              </span>
            )}
          </button>
        ))}
      </div>
      {tooltip && (
        <div
          className="fixed z-50 pointer-events-none animate-fade-in"
          style={{
            top: Math.max(8, tooltip.rect.top - 60),
            left: Math.min(window.innerWidth - 240, Math.max(8, tooltip.rect.left)),
            width: 220,
          }}
        >
          <div className="glass rounded-xl p-2 text-xs">
            <div className="text-white font-medium">{tooltip.item.name}</div>
            <div className="text-white/60 mt-0.5 leading-snug">
              {tooltip.item.description}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
